import React, { useState, useEffect } from 'react';
import { useTrainingSocket } from '../../context/TrainingSocketContext';
import { socketService } from '../../services/trainingSocketService';
import './DatasetViewDisplay.css';

interface DataSample {
  id: string;
  inputs: Array<{
    type: string;
    path?: string;
    data?: any;
  }>;
  outputs: Array<{
    type: string;
    data?: any;
    path?: string;
  }>;
  metadata?: {
    weight?: number;
    source?: string;
  };
}

const DatasetViewDisplay: React.FC = () => {
  const { socket, isConnected } = useTrainingSocket();
  const [samples, setSamples] = useState<DataSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isConnected || !socket) {
      return;
    }

    socketService.getDatasetOverview();

    const handleDatasetOverview = (data: any) => {
      console.log('DATASET DATA RECEIVED:', data);
      if (Array.isArray(data)) {
        setSamples(data);
        setLoading(false);
      } else if (data && data.status === 'success' && Array.isArray(data.data)) {
        setSamples(data.data);
        setLoading(false);
      } else if (data && data.status === 'error') {
        setError(data.message || 'Failed to load dataset');
        setLoading(false);
      } else {
        setError('Unknown data format');
        setLoading(false);
      }
    };

    socket.on('datasetOverview', handleDatasetOverview);

    return () => {
      socket.off('datasetOverview', handleDatasetOverview);
    };
  }, [isConnected, socket]);

  if (loading) {
    return (
      <div className="dataset-view-container">
        <div className="dataset-view-header">
          <h2 className="dataset-view-title">📊 Dataset Overview</h2>
        </div>
        <div className="dataset-view-loading">
          <p>Loading dataset...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dataset-view-container">
        <div className="dataset-view-header">
          <h2 className="dataset-view-title">📊 Dataset Overview</h2>
        </div>
        <div className="dataset-view-error">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dataset-view-container">
      <div className="dataset-view-header">
        <h2 className="dataset-view-title">📊 Dataset Overview</h2>
        <div className="dataset-view-stats">
          <span className="stat-item">
            <span className="stat-label">Total Samples:</span>
            <span className="stat-value">{samples.length}</span>
          </span>
        </div>
      </div>
      <div className="dataset-view-content">
        <div className="dataset-pairs-container">
          {samples.length === 0 ? (
            <div className="dataset-empty">
              <p>No dataset samples found.</p>
            </div>
          ) : (
            samples.map((sample, index) => {
              const inputPath = sample.inputs?.[0]?.path || 'N/A';
              const filename = inputPath.split('/').pop() || inputPath;
              const label = sample.outputs?.[0]?.data ?? 'N/A';
              
              return (
                <div key={sample.id || index} className="dataset-pair">
                  <div className="dataset-pair-header">
                    <span className="pair-id">Sample {index + 1}</span>
                    {sample.id && <span className="pair-sample-id">ID: {sample.id}</span>}
                  </div>
                  <div className="dataset-pair-content">
                    <div className="dataset-pair-input">
                      <div className="pair-section-header">Input</div>
                      <div className="pair-section-content">
                        <div className="dataset-item">
                          <div className="dataset-item-type">{sample.inputs?.[0]?.type || 'unknown'}</div>
                          <div className="dataset-item-path">
                            <div className="path-label">File:</div>
                            <div className="path-value">{filename}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="dataset-pair-divider"></div>
                    <div className="dataset-pair-output">
                      <div className="pair-section-header">Output / Label</div>
                      <div className="pair-section-content">
                        <div className="dataset-item">
                          <div className="dataset-item-type">{sample.outputs?.[0]?.type || 'unknown'}</div>
                          <div className="dataset-item-label">
                            <span className="label-badge">Label: {String(label)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default DatasetViewDisplay;
